/*
* Copyright (c) 2009-2022. Authors: see NOTICE file.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*      http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

import {Style, Stroke, Fill, Circle, Text} from 'ol/style';
import {MultiPoint} from 'ol/geom';
import {asArray as hexToRgb} from 'ol/color';
import constants from '@/utils/constants';

export const width = 2;

// -----

export function isCluster(feature) {
  let annot = feature.get('annot');
  if(!annot) {
    return;
  }
  return annot.count != null;
}

// -----

export function createStrokeStyle(color, opacity=0.5) {
  let colorArray = hexToRgb(color);
  let colorWithOpacity = colorArray.slice();
  colorWithOpacity[3] = opacity;

  let stroke = new Stroke({color: colorWithOpacity, width: 2});

  return new Style({
    stroke,
    image: new Circle({radius:5, stroke: stroke})
  });
}

export function createLineStrokeStyle(color, opacity=0.5) {
  let colorArray = hexToRgb(color);
  let colorWithOpacity = colorArray.slice();
  colorWithOpacity[3] = opacity;

  return new Style({stroke: new Stroke({color: colorWithOpacity, width: 5})});
}

// -----

function colorWithOpacity(color, opacity) {
  let colorWithOpacity = color.slice();
  colorWithOpacity[3] = opacity;
  return colorWithOpacity;
}

function createStroke() {
  let color = (constants.ANNOTATION_STROKE_COLOR) ? constants.ANNOTATION_STROKE_COLOR : black;
  return new Stroke({color: colorWithOpacity(color, constants.ANNOTATION_STROKE_OPACITY), width: 2});
}

function createCircleStyle(colorArray) {
  return new Circle({
    radius: 5,
    stroke: new Stroke({color: colorArray, width}),
  });
}

export function createColorStyle(color, opacity=0.5) {
  let colorArray = hexToRgb(color);
  let colorWithOpacity = colorArray.slice();
  colorWithOpacity[3] = opacity;

  let stroke = new Stroke({color: colorArray, width});

  return new Style({
    stroke,
    fill: new Fill({color: [0,0,0,0]}), // mandatory, otherwise not clickable
    image: createCircleStyle(colorArray)
  });
}

export function createColorStyleWithFill(color, opacity=0.5) {
  opacity = constants.DEFAULT_OPACITY;
  let colorArray = hexToRgb(color);
  let colorWithOpacity = colorArray.slice();
  colorWithOpacity[3] = opacity;

  let fill = new Fill({color: colorWithOpacity});

  let circleStyle = new Circle({
    radius: 5,
    fill: new Fill({color: colorArray}),
  });
  circleStyle.setOpacity(opacity);

  return new Style({
    fill,
    image: circleStyle
  });
}

// -----

export function createColorLineStyle(color, opacity=0.5) {
  let colorArray = hexToRgb(color);
  let colorWithOpacity = colorArray.slice();
  colorWithOpacity[3] = opacity;

  return new Style({ stroke: new Stroke({color: colorArray, width: width}) });
}

// -----

let textFill = new Fill({color: '#000000'});
let textStroke = new Stroke({color: '#000000', width: 3});
const backgroundFill = new Fill({color: 'rgba(255, 255, 255, 0.5)'});
const backgroundStroke = new Stroke({color: 'rgba(0, 0, 0, 0.5)', width: 1});

export function createTextBackground({
  fill = backgroundFill,
  stroke = backgroundStroke,
  padding = [5, 5, 5, 5]
} = {}) {
  return { fill, stroke, padding };
}

export function createTextStyle({text, fontSize = 22, fill = textFill, stroke = textStroke, offsetX = 0, offsetY = 0,
  align = 'center', justify = 'left', baseline = 'top', background }) {
  const textOptions = {
    text,
    font: `${fontSize}px Arial, sans-serif`,
    overflow: true,
    fill,
    stroke,
    offsetX,
    offsetY,
    textAlign: align,
    justify,
    textBaseline: baseline
  };

  if (background) {
    textOptions.backgroundFill = background.fill;
    textOptions.backgroundStroke = background.stroke;
    textOptions.padding = background.padding;
  }

  return new Style({
    text: new Text(textOptions)
  });
}

// -----



export const blue = [0, 153, 255, 1];
export const green = [51, 160, 79, 1];
export const lightGreen = [17, 214, 76, 1];
export const red = [200, 40, 40, 1];
export const lightRed = [255, 56, 56, 1];
export const white = [255, 255, 255, 1];
export const black = [0, 0, 0, 1];
export const gray = [128, 128, 128, 1];

export const blueStroke = new Stroke({color: blue, width: width});
export const greenStroke = new Stroke({color: green, width: width + 1});
export const lightGreenStroke = new Stroke({color: lightGreen, width: width});
export const redStroke = new Stroke({color: red, width: width + 1});
export const lightRedStroke = new Stroke({color: lightRed, width: width});
export const whiteStroke = new Stroke({color: white, width: width + 2});

let selectStroke = blueStroke;
if (constants.ANNOTATION_STROKE_SELECT_COLOR) {
  let color = colorWithOpacity(constants.ANNOTATION_STROKE_SELECT_COLOR, 1);
  selectStroke = new Stroke({color, width});
}

export let trackedSelectStyles = [
  new Style({ stroke: whiteStroke }),
  new Style({ image: new Circle({radius: 6, stroke: whiteStroke}) })
];

export let selectStyles = [
  new Style({ stroke: whiteStroke }),
  new Style({ image: new Circle({radius: 6, stroke: whiteStroke}) })
];

export let verticesStyle = new Style({
  image: new Circle({radius: width + 1, fill: new Fill({color: blue})}),
  geometry: function(feature) {
    // return the coordinates of the first ring of the polygon
    var coordinates = feature.getGeometry().getCoordinates()[0];
    return new MultiPoint(coordinates);
  }
});

export let reviewedStyles = [
  new Style({ stroke: greenStroke }),
  new Style({ image: new Circle({radius: 6, stroke: greenStroke}) })
];

export let reviewedSelectStyles = [
  new Style({ stroke: whiteStroke }),
  new Style({ stroke: lightGreenStroke }),
  new Style({ image: new Circle({radius: 6, stroke: lightGreenStroke}) })
];

export let rejectedStyles = [
  new Style({ stroke: redStroke }),
  new Style({ image: new Circle({radius: 6, stroke: redStroke}) })
];

export let rejectedSelectStyles = [
  new Style({ stroke: whiteStroke }),
  new Style({ stroke: lightRedStroke }),
  new Style({ image: new Circle({radius: 6, stroke: lightRedStroke}) })
];

// -----

export function changeOpacity(style, opacity) {

}

export function changeColor(style, becomeViolet) {
  let stroke = style.getStroke();
  if (stroke) {
    let circle = style.getImage();

    let currentColor = stroke.getColor();
    if (currentColor[0] === black[0] && currentColor[1] === black[1] && currentColor[2] === black[2] && !becomeViolet) {
      stroke.setColor(blue);
      if (circle) {
        style.setImage(createCircleStyle(blue));
      }
    }
    else if (currentColor[0] === blue[0] && currentColor[1] === blue[1] && currentColor[2] === blue[2] && becomeViolet) {
      stroke.setColor(black);
      if (circle) {
        style.setImage(createCircleStyle(black));
      }
    }
  }

  let fill = style.getFill();
  if (fill) {
    let currentColor = fill.getColor();
    if (currentColor[0] === black[0] && currentColor[1] === black[1] && currentColor[2] === black[2] && !becomeViolet) {
      fill.setColor(colorWithOpacity(blue, currentColor[3]));
    }
    else if (currentColor[0] === blue[0] && currentColor[1] === blue[1] && currentColor[2] === blue[2] && becomeViolet) {
      fill.setColor(colorWithOpacity(black, currentColor[3]));
    }
  }
}

// -----

function createDefaultColor(name, hexaCode) {
  return {
    name,
    fill: new Fill({color: '#' + hexaCode}),
    hexaCode
  };
}

export const defaultColors = Object.freeze([
  createDefaultColor('black', '000000'),
  createDefaultColor('white', 'ffffff'),
  createDefaultColor('red', 'ff0000'),
  createDefaultColor('orange', 'ff6600'),
  createDefaultColor('yellow', 'ffff00'),
  createDefaultColor('green', '008000'),
  createDefaultColor('blue', '0000ff'),
  createDefaultColor('purple', '800080')
]);
